import { PinIcon, UnPinIcon } from '@eleveo/icons-react';
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { DropdownSubmenu } from '../../../components/Dropdown/DropdownSubmenu';
import {
  PIN_ACTIVITIES_SUBMENU,
  PIN_ALL_ACTIVITIES,
  PIN_ALL_ACTIVITIES_IN_GIVEN_DAY,
  PIN_SELECTED_ACTIVITIES,
  SETTINGS,
  UNPIN_ACTIVITIES_SUBMENU,
  UNPIN_ALL_ACTIVITIES,
  UNPIN_ALL_ACTIVITIES_IN_GIVEN_DAY,
  UNPIN_SELECTED_ACTIVITIES,
} from '../constants';

export type Props = {
  className?: string;
  dataTestIdPrefix?: string;
  iconsClassName?: string;
  pinAllScheduleElements: () => void;
  pinElementsInCurrentDay: () => void;
  pinnableElementsCount: number;
  pinnableElementsInCurrentDayCount: number;
  pinnableSelectedElementsInCurrentDayCount: number;
  pinnedElementsCount: number;
  pinnedElementsInCurrentDayCount: number;
  pinnedSelectedElementsInCurrentDayCount: number;
  pinSelectedElements: () => void;
  unpinAllScheduleElements: () => void;
  unpinElementsInCurrentDay: () => void;
  unpinSelectedElements: () => void;
};

export const PinUnpinSubmenus: React.FC<Props> = ({
  className = 'dropdown-item',
  dataTestIdPrefix = SETTINGS,
  iconsClassName = 'ml-1 mr-3',
  pinAllScheduleElements,
  pinElementsInCurrentDay,
  pinnableElementsCount,
  pinnableElementsInCurrentDayCount,
  pinnableSelectedElementsInCurrentDayCount,
  pinnedElementsCount,
  pinnedElementsInCurrentDayCount,
  pinnedSelectedElementsInCurrentDayCount,
  pinSelectedElements,
  unpinAllScheduleElements,
  unpinElementsInCurrentDay,
  unpinSelectedElements,
}) => {
  const { t } = useTranslation();

  const noScheduleElementsLeftToBePinned = !pinnableElementsCount || pinnedElementsCount === pinnableElementsCount;

  const withToastMessage =
    (action: () => void, count: number, pin = true) =>
    () => {
      action();
      toast.info(t(`schedule.activityWas${pin ? 'Pinned' : 'Unpinned'}`, { count }));
    };

  return (
    <>
      <DropdownSubmenu
        className={className}
        dataTestId={`${dataTestIdPrefix}:${PIN_ACTIVITIES_SUBMENU}`}
        disabled={noScheduleElementsLeftToBePinned}
        items={[
          {
            'data-test-id': `${dataTestIdPrefix}:${PIN_SELECTED_ACTIVITIES}`,
            onClick: withToastMessage(pinSelectedElements, pinnableSelectedElementsInCurrentDayCount),
            className: classnames(className, {
              [`${className}--disabled`]: !pinnableSelectedElementsInCurrentDayCount,
            }),
            disabled:
              !pinnableSelectedElementsInCurrentDayCount ||
              pinnedSelectedElementsInCurrentDayCount === pinnableSelectedElementsInCurrentDayCount,
            children: t('schedule.pinSelectedActivity', { count: pinnableSelectedElementsInCurrentDayCount }),
            key: 'pin-selected-activities',
          },
          {
            'data-test-id': `${dataTestIdPrefix}:${PIN_ALL_ACTIVITIES_IN_GIVEN_DAY}`,
            onClick: withToastMessage(pinElementsInCurrentDay, pinnableElementsInCurrentDayCount),
            className: classnames(className, { [`${className}--disabled`]: !pinnableElementsInCurrentDayCount }),
            disabled:
              !pinnableElementsInCurrentDayCount ||
              pinnedElementsInCurrentDayCount === pinnableElementsInCurrentDayCount,
            children: t('schedule.pinAllActivitiesInGivenDay'),
            key: 'pin-all-activities-in-given-day',
          },
          {
            'data-test-id': `${dataTestIdPrefix}:${PIN_ALL_ACTIVITIES}`,
            onClick: withToastMessage(pinAllScheduleElements, pinnableElementsCount),
            className: classnames(className, { [`${className}--disabled`]: !pinnableElementsCount }),
            disabled: noScheduleElementsLeftToBePinned,
            children: t('schedule.pinAllActivitiesInWholeSchedule'),
            key: 'pin-all-activities',
          },
        ]}
      >
        <PinIcon
          className={classnames('text-xl', iconsClassName, {
            'text-chart-300': pinnableElementsCount,
          })}
        />
        <span>{t('schedule.pinActivity', { count: 2 })}</span>
      </DropdownSubmenu>
      <DropdownSubmenu
        className={className}
        dataTestId={`${dataTestIdPrefix}:${UNPIN_ACTIVITIES_SUBMENU}`}
        disabled={!pinnedElementsCount}
        items={[
          {
            'data-test-id': `${dataTestIdPrefix}:${UNPIN_SELECTED_ACTIVITIES}`,
            onClick: withToastMessage(unpinSelectedElements, pinnedSelectedElementsInCurrentDayCount, false),
            className: classnames(className, { [`${className}--disabled`]: !pinnedSelectedElementsInCurrentDayCount }),
            disabled: !pinnedSelectedElementsInCurrentDayCount,
            children: t('schedule.unpinSelectedActivity', { count: pinnedSelectedElementsInCurrentDayCount }),
            key: 'unpin-selected-activities',
          },
          {
            'data-test-id': `${dataTestIdPrefix}:${UNPIN_ALL_ACTIVITIES_IN_GIVEN_DAY}`,
            onClick: withToastMessage(unpinElementsInCurrentDay, pinnedElementsInCurrentDayCount, false),
            className: classnames(className, {
              [`${className}--disabled`]: !pinnedElementsInCurrentDayCount,
            }),
            disabled: !pinnedElementsInCurrentDayCount,
            children: t('schedule.unpinAllActivitiesInGivenDay'),
            key: 'unpin-all-activities-in-given-day',
          },
          {
            'data-test-id': `${dataTestIdPrefix}:${UNPIN_ALL_ACTIVITIES}`,
            onClick: withToastMessage(unpinAllScheduleElements, pinnedElementsCount, false),
            className: classnames(className, { [`${className}--disabled`]: !pinnedElementsCount }),
            disabled: !pinnedElementsCount,
            children: t('schedule.unpinAllActivitiesInWholeSchedule'),
            key: 'unpin-all-activities',
          },
        ]}
      >
        <UnPinIcon
          className={classnames('text-xl', iconsClassName, {
            'text-chart-300': pinnedElementsCount,
          })}
        />
        <span>{t('schedule.unpinActivity', { count: 2 })}</span>
      </DropdownSubmenu>
    </>
  );
};
